/**
 * useAnalytics Hook
 * 
 * Unified analytics facade — all form components should use ONLY this hook
 * instead of calling Mixpanel / GA / FB directly.
 */
import { useCallback } from 'react';
import Mixpanel from '../utils/mixpanel';
import GoogleAnalytics from '../utils/googleAnalytics';
import FacebookPixel from '../utils/facebookPixel';

const useAnalytics = () => {
  const trackEvent = useCallback((action, data = {}) => {
    if (!action) return;
    
    Mixpanel.track(action, data);
    
    try { GoogleAnalytics.event(action, data); }
    catch (e) { console.error('GA error:', e); }
    
    try { FacebookPixel.event(action, data); }
    catch (e) { console.error('FB Pixel error:', e); }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Analytics] ${action}`, data);
    }
  }, []);

  const trackButtonClick = useCallback((buttonName, extraData = {}) => {
    if (!buttonName) return;
    trackEvent('Button Click', { button_name: buttonName, ...extraData });
  }, [trackEvent]);

  const trackStepCompleted = useCallback((stepName, stepData = {}) => {
    if (!stepName) return;
    const sanitizedData = { ...stepData };
    ['password', 'email', 'phone', 'creditCard', 'cardNumber', 'cvv'].forEach(field => {
      delete sanitizedData[field];
    });
    trackEvent(`${stepName} Completed`, { step_name: stepName, ...sanitizedData });
  }, [trackEvent]);

  const trackStepStart = useCallback((stepName, stepData = {}) => {
    if (!stepName) return;
    trackEvent(`${stepName} Started`, { step_name: stepName, ...stepData });
  }, [trackEvent]);

  const trackError = useCallback((errorName, errorData = {}) => {
    if (!errorName) return;
    trackEvent('Error', { error_name: errorName, ...errorData });
  }, [trackEvent]);

  const trackConversion = useCallback((eventName, value) => {
    if (!eventName) return;
    Mixpanel.trackConversion(eventName, value);
    try {
      GoogleAnalytics.event(eventName, { value });
      FacebookPixel.event(eventName, { value });
    } catch (e) {
      console.error('Conversion tracking error:', e);
    }
  }, []);

  return {
    trackEvent,
    trackButtonClick,
    trackStepCompleted,
    trackStepStart,
    trackError,
    trackConversion
  };
};

export default useAnalytics; 